<template>
  <zoompinch ref="zoompinchRef" :width="840" :height="580" @update:scale="t => (scale = t)">
    <template #canvas>
      <div class="relative w-full h-full">
        <table-tab
          v-for="(table, index) of tables"
          :key="table.id"
          :table="table"
          :mode="mode"
          :time="currentTime"
          :show-actual-status="showActualStatus"
          :grouped-table-names="groupedTableNames[index]"
          :is-selected="selectedTables.includes(table.id)"
          :is-blocked="blockedTables.includes(table.id)"
          @selected-table="selectTable(table)"
          @assign-table="$emit('assignTable', $event)"
          @merge-tabs="$emit('mergeTabs', $event)"
          @swap-tables="$emit('swapTables', table.id)"
        />
      </div>
    </template>
  </zoompinch>
</template>

<script setup lang="ts">
import { useNow } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, useTemplateRef, watch } from 'vue'

import { useFloorplanStore } from '@/store/floorplan'
import { useTabsStore } from '@/store/tabs'
import type { Tab, TableWithTabs } from '@/types'

import TableTab from './Table.vue'
import zoompinch from './ZoomPinch/Zoompinch.vue'

type Props = {
  tables: TableWithTabs[]
  mode?: 'time' | 'amount' | 'diners' | 'reservation' | 'seats'
  wiggleEnabled?: boolean
  selectedTables?: string[]
  blockedTables?: string[]
  showActualStatus?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'time',
  selectedTables: () => [],
  blockedTables: () => [],
  showActualStatus: true
})

const emit = defineEmits<{
  tableSelected: [TableWithTabs]
  mergeTabs: [string]
  assignTable: [string[]]
  swapTables: [string]
}>()

const tabsStore = useTabsStore()
const floorplanStore = useFloorplanStore()

const { allTables } = storeToRefs(floorplanStore)
const { tabs } = storeToRefs(tabsStore)

const zoompinchRef = useTemplateRef('zoompinchRef')

const scale = ref(1)

const currentTime = useNow({
  interval: 60 * 1000
})

watch(
  () => zoompinchRef.value?.wrapperBounds,
  () => {
    requestAnimationFrame(() => {
      zoompinchRef.value?.applyTransform(1, [0.5, 0.5], [1, 1])
    })
  }
)

const groupedTableNames = computed<string[][]>(() => {
  return props.tables.map(table => {
    const tab = (tabs.value as Record<string, Tab>)[table.tabIds[0]]
    if (tab?.tables?.length > 1) {
      return allTables.value
        .filter(item => {
          return tab.tables.includes(item.id) && item.id !== table.id
        })
        .map(item => item.name)
    }
    return []
  })
})

function selectTable(table: TableWithTabs): void {
  if (table.type !== 'wall') {
    emit('tableSelected', table)
  }
}
</script>
